import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  FormControlLabel,
  Checkbox,
  TextField,
  Button,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Pagination,
  Backdrop,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  ButtonBase
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Edit, Visibility, Cancel } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from '../../../services/axios';
import { debounce } from 'lodash';

function ListaFornecedores() {
  const navigate = useNavigate();

  // Estados dos filtros
  const [termoBusca, setTermoBusca] = useState('');
  const [isAtivo, setIsAtivo] = useState(true);
  
  // Estados para listagem/paginação
  const [fornecedores, setFornecedores] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [loading, setLoading] = useState(false);
  const registrosPorPagina = 10;
  const API_URL = '/api/ComFornecedores';

  // Função que chama o back-end para buscar fornecedores
  const buscarFornecedores = async (busca, ativo, page) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/buscar`, {
        params: {
          q: busca,
          ativo,
          page: page - 1, // zero-based
          size: registrosPorPagina
        }
      });
      const data = response.data;
      setFornecedores(data.content);
      setTotalPaginas(data.totalPages);
    } catch (error) {
      console.error('Erro ao buscar fornecedores:', error);
    } finally {
      setLoading(false);
    }
  };

  // Debounce para evitar chamadas excessivas
  const debounceBuscar = useCallback(
    debounce((valorBusca, valorAtivo, paginaAtual) => {
      buscarFornecedores(valorBusca, valorAtivo, paginaAtual);
    }, 500),
    []
  );

  // useEffect para disparar a busca automaticamente ao alterar filtros ou paginação
  useEffect(() => {
    debounceBuscar(termoBusca, isAtivo, pagina);
  }, [termoBusca, isAtivo, pagina, debounceBuscar]);

  // Funções dos filtros
  const handleTermoBuscaChange = (e) => {
    setTermoBusca(e.target.value);
  };

  const handleAtivoChange = (e) => {
    setIsAtivo(e.target.checked);
    setPagina(1); // reseta para primeira página
  };

  const limparBusca = () => {
    setTermoBusca('');
    setIsAtivo(true);
    setPagina(1);
  };

  // Ações de navegação e edição
  const handleNovo = () => {
    navigate('/fornecedor/edit');
  };

  const handleEditar = (id) => {
    navigate(`/fornecedor/edit/${id}`);
  };

  const handleExcluir = async (id) => {
    console.log(`Excluir fornecedor com ID: ${id}`);
    // Exemplo de exclusão:
    // await axios.delete(`${API_URL}/${id}`);
    // buscarFornecedores(termoBusca, isAtivo, pagina);
  };

  // Paginação
  const handleChangePagina = (event, value) => {
    setPagina(value);
  };

  return (
    <Box sx={{ padding: { xs: 1, sm: 2 } }}>
      {/* Backdrop para indicar carregamento */}
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Cabeçalho da Página */}
      <Typography variant="h4" align="center" sx={{ mb: 3 }}>
        Listagem de Pessoa: Fornecedor
      </Typography>

      {/* Card de Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filtros
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2} alignItems="center">
            {/* Checkbox Ativo */}
            <Grid item xs={12} sm={2}>
              <FormControlLabel
                control={<Checkbox checked={isAtivo} onChange={handleAtivoChange} />}
                label="Ativo?"
              />
            </Grid>
            {/* Botão Limpar */}
            <Grid item xs={6} sm={1}>
              <Button variant="contained" onClick={limparBusca} fullWidth>
                Limpar
              </Button>
            </Grid>
            {/* Campo de Busca */}
            <Grid item xs={12} sm={9}>
              <TextField
                label="Nome, Fantasia, CPF, CNPJ"
                variant="outlined"
                fullWidth
                value={termoBusca}
                onChange={handleTermoBuscaChange}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Área de Ações: Botão "+ Novo" centralizado */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                      <Button variant="contained" onClick={()=>  navigate(`/clientes/edit`) }>
                        + Novo
                      </Button>
                    </Box>
          

      {/* Tabela de Resultados */}
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Código</strong></TableCell>
              <TableCell><strong>Nome / Fantasia</strong></TableCell>
              <TableCell><strong>Tipo</strong></TableCell>
              <TableCell><strong>CPF / CNPJ</strong></TableCell>
              <TableCell><strong>Cidade / Estado</strong></TableCell>
              <TableCell align="center"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fornecedores.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Nenhum fornecedor encontrado.
                </TableCell>
              </TableRow>
            ) : (
              fornecedores.map((fornecedor, idx) => (
                <TableRow key={idx}>
                  <TableCell>{fornecedor.id || fornecedor.codigo}</TableCell>
                  <TableCell>{fornecedor.nome}</TableCell>
                  <TableCell>{fornecedor.fisica ? 'Física' : 'Jurídica'}</TableCell>
                  <TableCell>{fornecedor.documento}</TableCell>
                  <TableCell>
                    {fornecedor.municipioNome} - {fornecedor.estadoSigla}
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                  <Box
                     sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    {/* Botão Editar: ícone e texto clicáveis */}
                    <ButtonBase
                      onClick={() => navigate(`/pessoa/fornecedor/edit/${fornecedor.id}`)}
                      sx={{
                        display: 'flex',
                        flexDirection: 'row', // força que seja sempre linha
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: 1,
                      }}
                    >
                      <Edit fontSize="small" sx={{ color: 'orange' }} /> <br/>
                      <Typography variant="caption" sx={{ mt: 0.5, color: 'orange' }}>
                        EDITAR
                      </Typography>
                    </ButtonBase>

                    {/* Botão Consultar */}
                    <ButtonBase
                      onClick={() => navigate(`/pessoa/fornecedor/edit/${fornecedor.id}`)}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        p: 1,
                      }}
                    >
                      <Visibility fontSize="small" sx={{ color: 'blue' }} />
                      <Typography variant="caption" sx={{ mt: 0.5, color: 'blue' }}>
                        CONSULTAR
                      </Typography>
                    </ButtonBase>

                    {/* Botão Deletar */}
                    <ButtonBase
                   //   onClick={() => confirmDelete(cliente.id)}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        p: 1,
                      }}
                    >
                      <Cancel fontSize="small" sx={{ color: 'red' }} />
                      <Typography variant="caption" sx={{ mt: 0.5, color: 'red' }}>
                        DELETAR
                      </Typography>
                    </ButtonBase>
                  </Box>
                </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação centralizada */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Pagination count={totalPaginas} page={pagina} onChange={handleChangePagina} color="primary" />
      </Box>
    </Box>
  );
}

export default ListaFornecedores;
